import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FC, ReactNode } from 'react';
import { v4 as uuid } from 'uuid';
import {
  storageCommodityUnitLabels,
  storageFlowUnitLabels,
  storageOfferStatusLabels,
} from 'src/apis/storage/mappings';
import type {
  StorageCommodityUnit,
  StorageFlowUnit,
  StorageOfferDetails,
  StorageOffersOverviewParams,
  StorageOfferStatus,
  StorageProductType,
  StorageTypeOfProduct,
} from 'src/apis/storage/types';
import {
  storageServiceRootCacheKey,
  type StorageServiceRootRequest,
} from 'src/apis/storage/use-storage-service-root';
import { EmptyValue } from 'src/components/data-display/empty-value';
import { FormatDate } from 'src/components/data-display/format-date';
import { FormatValue } from 'src/components/data-display/format-value';
import {
  StatusDisplay,
  type StatusProps,
  useStatusDisplayMode,
} from 'src/components/data-display/status-display';
import { Tooltip } from 'src/components/overlay/tooltip';
import type { Successful } from 'src/hooks/use-axios';
import { useAxiosCache } from 'src/hooks/use-axios/axios-cache';
import { stringifySearchParams } from 'src/hooks/use-page-params-from-schema';
import { FormatTimerForTable, useTimer } from 'src/hooks/use-timer';
import type { ProductComponentSchema } from 'src/pages/storage/offers-new/create/schema';
import { cn } from 'src/utils/cn';
import type { Currency } from 'src/utils/currencies';

export const STORAGE_OFFER_CURRENCY: Currency = 'EUR';

export function getLegacyOffersSearch(ssoShortNames: string[]): string {
  return stringifySearchParams({
    tsoShortName: ssoShortNames.map((shortName) => ({
      label: shortName,
      value: shortName,
    })),
  });
}

export function getLegacyFinishedOffersSearch(ssoShortNames: string[]): string {
  return stringifySearchParams({
    quickFilter: 'FINISHED',
    tsoShortName: ssoShortNames.map((shortName) => ({
      label: shortName,
      value: shortName,
    })),
  });
}

/** Default query when loading the public offers list (e.g. create-offer duplicate checks). */
export const storageOffersListDefaultParams: StorageOffersOverviewParams = {
  limit: 100,
  offset: 0,
  sortBy: 'OFFER_BUSINESS_ID',
  sortDirection: 'asc',
};

export function getStorageProductQuantityUnitLabel(
  typeOfProduct: StorageTypeOfProduct
): string {
  switch (typeOfProduct.typeOfProduct) {
    case 'SBU':
      return 'SBU';
    case 'STORAGE_VOLUME':
      return storageCommodityUnitLabels[typeOfProduct.unit];
    case 'INJECTION_CAPACITY':
    case 'WITHDRAWAL_CAPACITY':
      return storageFlowUnitLabels[typeOfProduct.unit];
  }
}

export function getStorageReservePriceSuffix(
  typeOfProduct: StorageTypeOfProduct
): string {
  const unitLabel = getStorageProductQuantityUnitLabel(typeOfProduct);
  return `/${unitLabel}/Runtime`;
}

const storageOfferStatusVariantMap: Record<
  StorageOfferStatus,
  StatusProps['variant']
> = {
  OPEN: 'ready',
  FINISHED: 'success',
  DELETED: 'inactive',
  PUBLISHED: 'warning',
  CREATED: 'warning',
  WAITING_FOR_NEXT_ROUND: 'warning',
  APPROVAL_PENDING: 'warning',
  UNSUCCESSFUL: 'error',
  CANCELLED: 'inactive',
  FAILED: 'error',
};

export function formatCommodityDisplay(
  value: { amount: number; unit: StorageCommodityUnit } | null | undefined
): ReactNode {
  if (!value) {
    return <EmptyValue />;
  }
  return (
    <>
      <FormatValue
        value={value.amount}
        label=""
        hideLabel
        type="exact-decimals"
      />{' '}
      {storageCommodityUnitLabels[value.unit]}
    </>
  );
}

export function formatCapacityDisplay(
  value: { amount: number; unit: StorageFlowUnit } | null | undefined
): ReactNode {
  if (!value) {
    return <EmptyValue />;
  }
  return (
    <>
      <FormatValue
        value={value.amount}
        label=""
        hideLabel
        type="exact-decimals"
      />{' '}
      {storageFlowUnitLabels[value.unit]}
    </>
  );
}

const Countdown: FC<{
  timestamp: string;
  storageServiceRoot: Successful<StorageServiceRootRequest>;
}> = ({ timestamp, storageServiceRoot }) => {
  const timer = useTimer(timestamp);
  const axiosCache = useAxiosCache();
  const mode = useStatusDisplayMode();

  if (!timer) return null;
  return (
    <div data-testid="storage-offer-countdown">
      <Tooltip
        content={<FormatDate value={timestamp} type="date-time" />}
        dataTestId="storage-offer-countdown-tooltip"
      >
        {(targetProps) => (
          <span
            {...targetProps}
            aria-label="Countdown exact time"
            className={cn(
              `
                relative
                after:absolute after:-right-2xs after:content-['…']
              `,
              mode === 'page' ? `after:top-xs` : `after:top-2xs`,
              `
                after:text-label-sm after:font-label-semibold
                after:text-foreground-primary-inverted
              `,
              `
                legacy:after:text-[12px] legacy:after:leading-[17.1429px]
                legacy:after:font-[800] legacy:after:text-legacy-brand-secondary
              `
            )}
          >
            <FormatTimerForTable
              timer={timer}
              onElapsedRefresh={() => {
                axiosCache.invalidate(storageServiceRootCacheKey);
                storageServiceRoot.refresh?.();
              }}
            />
          </span>
        )}
      </Tooltip>
    </div>
  );
};

export const StorageOfferStatusWithTimer: FC<{
  status: StorageOfferStatus;
  storageServiceRoot: Successful<StorageServiceRootRequest>;
  cancellationReason?: string | null;
  countdownEnd?: string | null;
  'data-testid'?: string;
}> = ({
  status,
  storageServiceRoot,
  cancellationReason,
  countdownEnd,
  'data-testid': testId,
}) => {
  const displayTestId = testId ?? 'status-display';

  return (
    <div
      className={`
        inline-grid grid-flow-col items-center justify-start gap-xs
        legacy:gap-md
      `}
    >
      <StatusDisplay
        variant={storageOfferStatusVariantMap[status]}
        status={storageOfferStatusLabels[status]}
        data-testid={displayTestId}
        appendix={
          countdownEnd && (
            <Countdown
              timestamp={countdownEnd}
              storageServiceRoot={storageServiceRoot}
            />
          )
        }
      />
      {cancellationReason ? (
        <Tooltip
          dataTestId={`${displayTestId}-cancellation-reason-tooltip`}
          content={cancellationReason}
        >
          {(targetProps) => (
            <FontAwesomeIcon {...targetProps} icon={faInfoCircle} />
          )}
        </Tooltip>
      ) : null}
    </div>
  );
};
export function mapTypeOfProductToForm(top: StorageTypeOfProduct): {
  productType: StorageProductType;
  typeOfProductUnit: string;
} {
  switch (top.typeOfProduct) {
    case 'STORAGE_VOLUME':
      return { productType: 'storageVolume', typeOfProductUnit: top.unit };
    case 'INJECTION_CAPACITY':
      return { productType: 'injectionCapacity', typeOfProductUnit: top.unit };
    case 'WITHDRAWAL_CAPACITY':
      return {
        productType: 'withdrawalCapacity',
        typeOfProductUnit: top.unit,
      };
    case 'SBU':
      return { productType: 'SBU', typeOfProductUnit: top.unit };
  }
}

export function mapProductComponentsFromDetails(
  productComponents: StorageOfferDetails['productComponents']
): ProductComponentSchema[] {
  return productComponents.map((pc) => ({
    id: uuid(),
    capacityCategory: pc.capacityCategory,
    runtimeStart: pc.runtime.start,
    runtimeEnd: pc.runtime.end,
    storageVolumeAmount: pc.storageVolume?.amount ?? null,
    storageVolumeUnit: pc.storageVolume?.unit ?? null,
    injectionCapacityAmount: pc.injectionCapacity?.amount ?? null,
    injectionCapacityUnit: pc.injectionCapacity?.unit ?? null,
    withdrawalCapacityAmount: pc.withdrawalCapacity?.amount ?? null,
    withdrawalCapacityUnit: pc.withdrawalCapacity?.unit ?? null,
  }));
}
